import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { ErrorNote } from '@/ui/ErrorNote';
import { useAppUpdate } from './appUpdateContext';

/** O aviso de que há uma versão nova do app, com o passo a passo de atualizar (tudo sem sair do app, até o instalador do Android). */
export function UpdateDialog() {
  const update = useAppUpdate();
  const { state } = update;

  if (!('latest' in state)) {
    return null;
  }

  const { latest } = state;
  const working = state.kind === 'downloading';

  return (
    <Dialog
      open
      title="Há uma versão nova do app"
      onClose={working ? () => undefined : update.dismiss}
      actions={<Actions />}
    >
      {state.kind === 'available' || state.kind === 'error' ? (
        <p>
          A versão <strong>{latest.version}</strong> do Ronateia Games está disponível (você usa a{' '}
          {update.currentVersion}). Atualizar leva um minutinho e você não perde nada: sua conta e seus grupos
          continuam como estão.
        </p>
      ) : (
        <p>
          Atualizando para a versão <strong>{latest.version}</strong> (você usa a {update.currentVersion}).
        </p>
      )}

      {latest.notes ? (
        <p>
          <strong>O que há de novo:</strong> {latest.notes}
        </p>
      ) : null}

      {state.kind === 'needsPermission' ? (
        <p className="muted">
          Para instalar, o Android pede que você libere este app para instalar atualizações (só na primeira
          vez). Toque em &quot;Abrir configurações&quot;, ative &quot;Permitir desta fonte&quot; (ou
          &quot;Autorizar permissão&quot;) e volte: a atualização continua sozinha.
        </p>
      ) : null}

      {state.kind === 'downloading' ? (
        <div role="status" aria-live="polite">
          <progress
            max={100}
            value={state.progress === null ? undefined : Math.round(state.progress * 100)}
            aria-label="Baixando a atualização"
            style={{ width: '100%' }}
          />
          <p className="muted">
            {state.progress === null ? 'Baixando…' : `Baixando… ${Math.round(state.progress * 100)}%`}
          </p>
        </div>
      ) : null}

      {state.kind === 'installing' ? (
        <p role="status">
          Pronto! O Android vai pedir para você confirmar a instalação. Depois de instalar, é só abrir o
          Ronateia Games de novo.
          <br />
          <span className="muted">
            Se o Google Play Protect pedir uma verificação, toque em &quot;Verificar app&quot; (ou, em
            &quot;Mais detalhes&quot;, em &quot;Instalar sem análise&quot;). O celular pode pedir a sua
            digital ou senha.
          </span>
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <ErrorNote error={new Error(state.message)} fallback={state.message} />
      ) : null}
    </Dialog>
  );
}

function Actions() {
  const update = useAppUpdate();
  const { state } = update;

  switch (state.kind) {
    case 'available':
      return (
        <>
          <Button variant="go" icon="download" onClick={update.start}>
            Atualizar agora
          </Button>
          <Button variant="light" size="sm" onClick={update.dismiss}>
            Depois
          </Button>
        </>
      );
    case 'needsPermission':
      return (
        <>
          <Button variant="go" icon="key" onClick={update.openInstallSettings}>
            Abrir configurações
          </Button>
          <Button variant="light" size="sm" onClick={update.dismiss}>
            Depois
          </Button>
        </>
      );
    case 'downloading':
      return null;
    case 'installing':
      return (
        <Button variant="light" size="sm" onClick={update.dismiss}>
          Fechar
        </Button>
      );
    case 'error':
      return (
        <>
          <Button variant="go" icon="refresh" onClick={update.start}>
            Tentar de novo
          </Button>
          <Button variant="light" size="sm" onClick={update.openInBrowser}>
            Baixar pelo navegador
          </Button>
          <Button variant="light" size="sm" onClick={update.dismiss}>
            Depois
          </Button>
        </>
      );
    default:
      return null;
  }
}
